import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  APIResponse,
  CalendarEventsResponse,
  CalendarEventsFilters,
  CalendarStatsSummary,
  CalendarStatsFilters,
  LiveSessionDto,
  CreateLiveSessionRequest,
  UpdateLiveSessionRequest,
  LiveSessionsFilters,
  LiveSessionsListResponse,
  LiveSessionRegistrationRequest
} from '@/types/calendar.types';

class CalendarService {
  // ==========================================
  // CALENDAR EVENTS OPERATIONS
  // ==========================================

  /**
   * Get unified calendar events with flexible filtering
   * Supports both period-based (day/week/month) and custom date range filtering
   * If start_date and end_date are provided, they override the period parameter
   * Response is wrapped in { success, code, data } structure
   */
  async getCalendarEventsByPeriod(
    period: 'day' | 'week' | 'month',
    courseId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<any> {
    const params = new URLSearchParams();

    // Required parameter
    params.set('period', period);

    // Optional date range parameters (override period if both provided)
    if (startDate && endDate) {
      params.set('start_date', startDate);
      params.set('end_date', endDate);
    }

    // Optional course filter
    if (courseId) params.set('course_id', courseId);

    try {
      const response = await apiClient.get<any>(
        `${API_ENDPOINTS.CALENDAR_EVENTS.LIST}?${params.toString()}`
      );

      // Handle wrapped response structure { success, code, data }
      if (response.data?.data) {
        return response.data.data;
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching calendar events by period:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch calendar events');
    }
  }

  /**
   * Get unified calendar events from all sources (legacy - uses date range)
   * This is kept for backward compatibility but deprecated
   */
  async getCalendarEvents(filters: CalendarEventsFilters): Promise<CalendarEventsResponse> {
    const params = new URLSearchParams();

    // Required parameters
    params.set('start_date', filters.start_date);
    params.set('end_date', filters.end_date);

    // Optional parameters
    if (filters.event_types) params.set('event_types', filters.event_types);
    if (filters.include_completed !== undefined) {
      params.set('include_completed', filters.include_completed.toString());
    }
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.course_id) params.set('course_id', filters.course_id);
    if (filters.status_filter) params.set('status_filter', filters.status_filter);

    try {
      const response = await apiClient.get<CalendarEventsResponse>(
        `${API_ENDPOINTS.CALENDAR_EVENTS.LIST}?${params.toString()}`
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching calendar events:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch calendar events');
    }
  }

  /**
   * Get global calendar statistics (all events, all time)
   * Statistics are completely unfiltered and do not accept any parameters
   * Response is wrapped in { success, code, data } structure
   */
  async getCalendarStatsByPeriod(): Promise<any> {
    try {
      const response = await apiClient.get<any>(
        API_ENDPOINTS.CALENDAR_EVENTS.STATS
      );

      // Handle wrapped response structure { success, code, data }
      if (response.data?.data) {
        return response.data.data;
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching calendar stats:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch calendar statistics');
    }
  }

  /**
   * Get calendar statistics for dashboard (legacy - will be deprecated)
   */
  async getCalendarStats(filters?: CalendarStatsFilters): Promise<CalendarStatsSummary> {
    const params = new URLSearchParams();

    if (filters?.period) params.set('period', filters.period);
    if (filters?.course_id) params.set('course_id', filters.course_id);
    if (filters?.include_completed !== undefined) {
      params.set('include_completed', filters.include_completed.toString());
    }

    try {
      const response = await apiClient.get<{ summary: CalendarStatsSummary }>(
        `${API_ENDPOINTS.CALENDAR_EVENTS.STATS}?${params.toString()}`
      );

      return response.data.summary;
    } catch (error: any) {
      console.error('Error fetching calendar stats:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch calendar statistics');
    }
  }

  /**
   * Get today's calendar events
   */
  async getTodayEvents(): Promise<CalendarEventsResponse> {
    try {
      const response = await apiClient.get<CalendarEventsResponse>(
        API_ENDPOINTS.CALENDAR_EVENTS.TODAY
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching today\'s events:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch today\'s events');
    }
  }

  /**
   * Get this week's calendar events
   */
  async getThisWeekEvents(): Promise<CalendarEventsResponse> {
    try {
      const response = await apiClient.get<CalendarEventsResponse>(
        API_ENDPOINTS.CALENDAR_EVENTS.THIS_WEEK
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching this week\'s events:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch this week\'s events');
    }
  }

  /**
   * Get calendar events for a specific course
   */
  async getCourseEvents(courseId: string): Promise<CalendarEventsResponse> {
    try {
      const response = await apiClient.get<CalendarEventsResponse>(
        API_ENDPOINTS.CALENDAR_EVENTS.BY_COURSE(courseId)
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching course events:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch course events');
    }
  }

  // ==========================================
  // LIVE SESSIONS OPERATIONS
  // ==========================================

  /**
   * Create a new live session
   */
  async createLiveSession(request: CreateLiveSessionRequest): Promise<LiveSessionDto> {
    try {
      const response = await apiClient.post<LiveSessionDto>(
        API_ENDPOINTS.LIVE_SESSIONS.CREATE,
        request
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error creating live session:', error);
      throw new Error(error?.response?.data?.message || 'Failed to create live session');
    }
  }

  /**
   * Get list of live sessions with filters
   */
  async getLiveSessions(filters?: LiveSessionsFilters): Promise<LiveSessionsListResponse> {
    const params = new URLSearchParams();
    
    if (filters?.status) params.set('status', filters.status);
    if (filters?.instructor_id) params.set('instructor_id', filters.instructor_id);
    if (filters?.start_date) params.set('start_date', filters.start_date);
    if (filters?.end_date) params.set('end_date', filters.end_date);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.page) params.set('page', filters.page.toString());
    if (filters?.limit) params.set('limit', filters.limit.toString());
    
    try {
      const response = await apiClient.get<LiveSessionsListResponse>(
        `${API_ENDPOINTS.LIVE_SESSIONS.LIST}?${params.toString()}`
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching live sessions:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch live sessions');
    }
  }

  /**
   * Get live session by ID
   */
  async getLiveSessionById(sessionId: string): Promise<LiveSessionDto> {
    try {
      const response = await apiClient.get<LiveSessionDto>(
        API_ENDPOINTS.LIVE_SESSIONS.GET_BY_ID(sessionId)
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching live session:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch live session');
    }
  }

  /**
   * Update live session
   */
  async updateLiveSession(sessionId: string, request: UpdateLiveSessionRequest): Promise<LiveSessionDto> {
    try {
      const response = await apiClient.put<LiveSessionDto>(
        API_ENDPOINTS.LIVE_SESSIONS.UPDATE(sessionId),
        request
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error updating live session:', error);
      throw new Error(error?.response?.data?.message || 'Failed to update live session');
    }
  }

  /**
   * Delete live session
   */
  async deleteLiveSession(sessionId: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete<{ message: string }>(
        API_ENDPOINTS.LIVE_SESSIONS.DELETE(sessionId)
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error deleting live session:', error);
      throw new Error(error?.response?.data?.message || 'Failed to delete live session');
    }
  }

  /**
   * Register for a live session
   */
  async registerForSession(sessionId: string, request: LiveSessionRegistrationRequest): Promise<LiveSessionDto> {
    try {
      const response = await apiClient.post<LiveSessionDto>(
        API_ENDPOINTS.LIVE_SESSIONS.REGISTER(sessionId),
        request
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error registering for session:', error);
      throw new Error(error?.response?.data?.message || 'Failed to register for session');
    }
  }

  /**
   * Unregister from a live session
   */
  async unregisterFromSession(sessionId: string, userId: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete<{ message: string }>(
        API_ENDPOINTS.LIVE_SESSIONS.UNREGISTER(sessionId, userId)
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error unregistering from session:', error);
      throw new Error(error?.response?.data?.message || 'Failed to unregister from session');
    }
  }

  /**
   * Get user's live sessions
   */
  async getUserSessions(userId: string): Promise<LiveSessionsListResponse> {
    try {
      const response = await apiClient.get<LiveSessionsListResponse>(
        API_ENDPOINTS.LIVE_SESSIONS.USER_SESSIONS(userId)
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching user sessions:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch user sessions');
    }
  }

  /**
   * Get today's live session schedule
   */
  async getTodaySchedule(): Promise<LiveSessionsListResponse> {
    try {
      const response = await apiClient.get<LiveSessionsListResponse>(
        API_ENDPOINTS.LIVE_SESSIONS.TODAY_SCHEDULE
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching today\'s schedule:', error);
      throw new Error(error?.response?.data?.message || 'Failed to fetch today\'s schedule');
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Format date for API parameters
   */
  formatDateForAPI(date: Date): string {
    return date.toISOString();
  }

  /**
   * Get date range for current week
   */
  getCurrentWeekRange(): { start_date: string; end_date: string } {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    return {
      start_date: this.formatDateForAPI(startOfWeek),
      end_date: this.formatDateForAPI(endOfWeek)
    };
  }

  /**
   * Get date range for current month
   */
  getCurrentMonthRange(): { start_date: string; end_date: string } {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    
    return {
      start_date: this.formatDateForAPI(startOfMonth),
      end_date: this.formatDateForAPI(endOfMonth)
    };
  }
}

// Export singleton instance
export const calendarService = new CalendarService();